import { useState, useEffect } from 'react'
import {
  PRACTICE_AREAS,
  PRACTICE_AREAS_DISPLAY_NAMES,
  PRACTISING_AOL_OTHER
} from 'constants/aol/aol'
import classNames from 'classnames'
import { useSelector } from 'react-redux'
import { selectPractisingAol } from 'redux/selectors/selectPractisingAol'
import InfoToolTipIcon from './InfoToolTipIcon'
import { Grid } from '@mui/material'
import Dropdown from 'components/Dropdown'
import TextInput from 'components/TextInput'
import { PRACTISING_AOL } from 'constants/requests'
import { updatePractisingAol } from 'services/SessionService'
import ControlButtons from './ControlButtons'
import { useAolUpdate } from 'hooks/useAolUpdate'
import { AOL_TOOLTIP_TEXT } from 'helpers/aol/aolTooltipText'
import { getPractisingAolDropdownOptions } from 'helpers/aol/aolHelpers'

function getCustomValue (aol: string): string {
  return aol !== '' && !PRACTICE_AREAS.includes(aol) ? aol : ''
}

export default function PractisingAolSelector (): JSX.Element {
  const currentPractisingAol = useSelector(selectPractisingAol)

  const [isEditingOther, setIsEditingOther] = useState(false)
  const [customPractisingAol, setCustomPractisingAol] = useState(
    () => getCustomValue(currentPractisingAol)
  )

  useEffect(() => {
    setIsEditingOther(false)
    setCustomPractisingAol(getCustomValue(currentPractisingAol))
  }, [currentPractisingAol])

  const wrappedUpdatePractisingAol = (val: string): ReturnType<typeof updatePractisingAol> =>
    updatePractisingAol(val === PRACTISING_AOL_OTHER ? customPractisingAol.trim() : val)

  const { selectedAol, changed, handleChange, handleSave, handleCancel } = useAolUpdate({
    currentValue: currentPractisingAol,
    updateFunction: wrappedUpdatePractisingAol,
    refreshRequest: PRACTISING_AOL,
    successMessage: 'Practising area saved'
  })

  const handleDropdownChange = (val: string): void => {
    setIsEditingOther(val === PRACTISING_AOL_OTHER)
    handleChange(val)
  }

  const handleCancelWithReset = (): void => {
    setIsEditingOther(false)
    setCustomPractisingAol(getCustomValue(currentPractisingAol))
    handleCancel()
  }

  const isChanged = changed || isEditingOther
  const saveDisabled = isEditingOther && customPractisingAol.trim() === ''

  const practisingAolDropdownOptions = getPractisingAolDropdownOptions(selectedAol)

  const dropdownLabel = isEditingOther
    ? 'Other'
    : selectedAol !== ''
      ? (PRACTICE_AREAS_DISPLAY_NAMES[selectedAol] !== '' ? PRACTICE_AREAS_DISPLAY_NAMES[selectedAol] : selectedAol)
      : null

  return (
    <div className='h10 mb10'>
      <Grid container className='flex items-center'>
        <Grid item className='mr2'>
          <InfoToolTipIcon
            title={AOL_TOOLTIP_TEXT.practising}
          />
        </Grid>
        <h3>Practising Area of Law</h3>
      </Grid>
      <div className='bg-white ph5 pv6 flex items-center ma0 lighter'>
        <div className='w-50 pr6 relative'>
          <Dropdown
            align='left'
            options={practisingAolDropdownOptions}
            handleOnSelect={(val) => handleDropdownChange(val as string)}
            showSelectedIcons={false}
            label={(): JSX.Element => (
              <div className='w-100 truncate tl'>
                {dropdownLabel ?? <span className='grey'>Select an option</span>}
              </div>
            )}
            classes={{
              button: 'w-100 h2 bg-white ba b--bright-grey br2 justify-between',
              chevron: 'black',
              menu: 'bg-white',
              option: ({ focus }) => classNames(
                'black no-underline',
                { 'bg-casper-white': focus, 'bg-white': !focus }
              )
            }}
          />
          {isEditingOther && (
            <div className='mt4'>
              <TextInput
                placeholder='Enter your practice area'
                value={customPractisingAol}
                handleOnChange={setCustomPractisingAol}
              />
            </div>
          )}
        </div>
      </div>
      <ControlButtons
        changed={isChanged}
        handleSave={handleSave}
        handleCancel={handleCancelWithReset}
        saveDisabled={saveDisabled}
      />
    </div>
  )
}
